/**
 * 🔒 P2 보안 개선: 세션 타임아웃 경고 모달
 *
 * 세션 만료 5분 전에 사용자에게 경고
 * 세션 연장 또는 로그아웃 선택 가능
 */

import React from 'react';

interface SessionTimeoutWarningProps {
  isVisible: boolean;
  remainingTime: number | null;
  onExtend: () => void;
  onLogout: () => void;
}

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  isVisible,
  remainingTime,
  onExtend,
  onLogout
}) => {
  if (!isVisible || remainingTime === null) return null;

  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        {/* 경고 아이콘 */}
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-yellow-100 p-3">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* 제목 */}
        <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
          세션이 곧 만료됩니다
        </h2>

        {/* 설명 */}
        <p className="text-center text-gray-600 mb-4">
          보안을 위해 {minutes > 0 && `${minutes}분 `}{seconds}초 후 자동으로 로그아웃됩니다.
        </p>

        {/* 남은 시간 표시 */}
        <div className="text-center mb-6">
          <span className="text-3xl font-bold text-indigo-600">
            {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
          </span>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={onExtend}
            className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors font-medium"
          >
            세션 연장하기
          </button>
          <button
            onClick={onLogout}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            지금 로그아웃
          </button>
        </div>

        {/* 안내 문구 */}
        <p className="text-xs text-gray-500 text-center mt-4">
          활동을 재개하면 세션이 자동으로 연장됩니다.
        </p>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
